import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, createSlots as _createSlots } from "vue"

import { watch, computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { EventObject } from "cytoscape";
import { DetailsOpenState } from "@/types";
import {
  appStore,
  artifactStore,
  traceStore,
  deltaStore,
  selectionStore,
  layoutStore,
  timStore,
  cyStore,
  traceApiStore,
  traceMatrixApiStore,
} from "@/hooks";
import { Routes } from "@/router";
import { Cytoscape } from "./base";
import { ArtifactNode, TraceLink, ArtifactMenu } from "./artifact";
import { TimNode, TimLink, TimMenu } from "./tim";
import { EmptyGraphButtons, GraphButtons, GraphFab } from "./button";


/**
 * Renders a tree of project data.
 * Will either render the TIM tree, or artifacts and trace links, depending on the graph mode.
 */
const __default__ = {
  name: "ProjectTree",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const currentRoute = useRoute();

const graph = ref(
  cyStore.buildProjectGraph({
    canCreateTrace: (source, target) =>
      traceStore.isLinkAllowed(source.data(), target.data()) === true,
    handleCreateTrace: (source, target) => {
      if (source.data()?.graph === "tree") {
        traceApiStore.handleCreate(source.data(), target.data());
      } else {
        traceMatrixApiStore.handleCreate(
          source.data().artifactType,
          target.data().artifactType
        );
      }
    },
  })
);

const isInView = computed(
  () => layoutStore.isTreeMode || layoutStore.isTimMode
);
const isTreeMode = computed(() => layoutStore.isTreeMode);

const artifacts = computed(() => artifactStore.currentArtifacts);
const artifactsInView = computed(() => artifactStore.artifactsInView);

const traceLinks = computed(() =>
  deltaStore.inDeltaView ? traceStore.currentTraces : traceStore.visibleTraces
);

const artifactTypes = computed(() => timStore.artifactTypes);
const traceMatrices = computed(() => timStore.traceMatrices);

const className = computed(() => {
  if (!isInView.value) {
    return "artifact-view disabled";
  } else if (!appStore.isLoading) {
    return "artifact-view visible";
  } else {
    return "artifact-view";
  }
});

/**
 * Handles a click event on the graph.
 * When a click is registered on the background:
 * - Draw mode is disabled.
 * - Selections are cleared if a save panel is not open.
 * @param event - The click event.
 */
function handleClick(event: EventObject): void {
  if (event.target !== event.cy) return;

  if (appStore.popups.drawTrace) {
    cyStore.drawMode("disable");
  }

  if (
    (
      [
        "document",
        "saveArtifact",
        "saveTrace",
        "generateArtifact",
        "generateTrace",
      ] as DetailsOpenState[]
    ).includes(appStore.popups.detailsPanel)
  )
    return;

  selectionStore.clearSelections();
}

function handleReset(): void {
  selectionStore.clearSelections();
  layoutStore.setGraphLayout();
}

onMounted(() => handleReset());

/** Resets the layout when the route changes. */
watch(
  () => currentRoute.path,
  () => {
    if (currentRoute.path !== Routes.ARTIFACT) return;

    handleReset();
  }
);

watch(
  () => isInView.value,
  (inView) => {
    if (!inView) return;

    handleReset();
  }
);

watch(
  () => isTreeMode.value,
  () => handleReset()
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(EmptyGraphButtons)),
    _createVNode(_unref(GraphButtons)),
    _createVNode(_unref(GraphFab)),
    _createVNode(_unref(Cytoscape), {
      id: "cytoscape-artifact",
      graph: graph.value,
      class: _normalizeClass(className.value),
      "data-cy": "view-artifact-tree",
      onClick: handleClick
    }, _createSlots({
      default: _withCtx(() => [
        (isTreeMode.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(artifacts.value, (artifact) => {
                return (_openBlock(), _createBlock(_unref(ArtifactNode), {
                  key: artifact.id,
                  artifact: artifact,
                  "artifacts-in-view": artifactsInView.value
                }, null, 8, ["artifact", "artifacts-in-view"]))
              }), 128)),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(traceLinks.value, (traceLink) => {
                return (_openBlock(), _createBlock(_unref(TraceLink), {
                  key: traceLink.traceLinkId,
                  trace: traceLink,
                  "artifacts-in-view": artifactsInView.value
                }, null, 8, ["trace", "artifacts-in-view"]))
              }), 128))
            ], 64))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(artifactTypes.value, (type) => {
                return (_openBlock(), _createBlock(_unref(TimNode), {
                  key: type.typeId,
                  count: type.count,
                  "artifact-type": type.name,
                  icon: type.icon
                }, null, 8, ["count", "artifact-type", "icon"]))
              }), 128)),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(traceMatrices.value, (matrix) => {
                return (_openBlock(), _createBlock(_unref(TimLink), {
                  key: matrix.id,
                  count: matrix.count,
                  "target-type": matrix.targetType,
                  "source-type": matrix.sourceType,
                  generated: matrix.generatedCount > 0
                }, null, 8, ["count", "target-type", "source-type", "generated"]))
              }), 128))
            ], 64))
      ]),
      _: 2
    }, [
      (isTreeMode.value)
        ? {
            name: "context-menu",
            fn: _withCtx(() => [
              _createVNode(_unref(ArtifactMenu))
            ]),
            key: "0"
          }
        : {
            name: "context-menu",
            fn: _withCtx(() => [
              _createVNode(_unref(TimMenu))
            ]),
            key: "1"
          }
    ]), 1032, ["graph", "class"])
  ], 64))
}
}

})